if (!customElements.get('nh-search-form')) {
  class NhSearchForm extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.drawer = this.closest('sidebar-drawer');
      this.input = this.querySelector('input[type="search"]');
      this.form = this.querySelector('form');
      this.output = this.querySelector('[data-nh-search-results]');
      this.initial = this.querySelector('[data-nh-search-initial]');
      this.clear = this.querySelector('[data-nh-search-clear]');
      this.status = this.querySelector('[role="status"]');
      this.scroll = this.querySelector('[data-nh-search-scroll]');
      this.options = [];
      this.activeIndex = -1;
      this.cache = new Map();
      this.requestId = 0;
      this.opened = false;
      this.onOpener = (event) => {
        const opener = event.target.closest('[data-js-open-search-drawer]');
        if (opener) this.opener = opener;
      };
      this.onDialogKey = this.handleDialogKey.bind(this);
      document.addEventListener('click', this.onOpener, true);
      this.drawer.addEventListener('keydown', this.onDialogKey);
      this.input.addEventListener('input', () => {
        this.controller?.abort();
        this.requestId += 1;
        this.output.removeAttribute('aria-busy');
        this.clear.hidden = this.input.value.length === 0;
        this.setActive(-1);
        this.options = [];
        this.input.setAttribute('aria-expanded', 'false');
        clearTimeout(this.timer);
        if (!this.input.value.trim()) {
          this.resetResults();
        } else {
          this.timer = setTimeout(() => this.search(this.input.value.trim()), 180);
        }
      });
      this.input.addEventListener('keydown', (event) => {
        if (event.isComposing) return;
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          if (!this.options.length) return;
          const next = event.key === 'ArrowDown'
            ? Math.min(this.activeIndex + 1, this.options.length - 1)
            : Math.max(this.activeIndex - 1, -1);
          this.setActive(next, true);
        } else if (event.key === 'Enter' && this.activeIndex >= 0) {
          event.preventDefault();
          this.options[this.activeIndex].click();
        }
      });
      this.form.addEventListener('submit', (event) => {
        if (!this.input.value.trim()) {
          event.preventDefault();
          this.input.focus();
        }
      });
      this.clear.addEventListener('click', () => {
        this.input.value = '';
        this.clear.hidden = true;
        this.resetResults();
        this.input.focus();
      });
      this.observer = new MutationObserver(() => {
        const opened = this.drawer.classList.contains('sidebar--opened');
        if (opened === this.opened) return;
        this.opened = opened;
        if (opened) {
          this.input.focus({ preventScroll: true });
          if (this.input.value.trim()) this.search(this.input.value.trim());
        } else {
          clearTimeout(this.timer);
          this.controller?.abort();
          this.output.removeAttribute('aria-busy');
          this.input.setAttribute('aria-expanded', 'false');
          if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
        }
      });
      this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.observer?.disconnect();
      document.removeEventListener('click', this.onOpener, true);
      this.drawer?.removeEventListener('keydown', this.onDialogKey);
    }

    handleDialogKey(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.drawer.querySelector('[data-js-close]').click();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.drawer.querySelectorAll('a[href],button,input,[tabindex="0"]'))
        .filter((element) => !element.disabled && !element.closest('[hidden]') && element.getClientRects().length);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    setActive(index, reveal = false) {
      this.options.forEach((option, position) => {
        const selected = position === index;
        option.setAttribute('aria-selected', String(selected));
        option.classList.toggle('is-active', selected);
      });
      this.activeIndex = index;
      if (index >= 0 && this.options[index]) {
        this.input.setAttribute('aria-activedescendant', this.options[index].id);
        if (reveal) this.options[index].scrollIntoView({ block: 'nearest' });
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    resetResults() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.requestId += 1;
      this.setActive(-1);
      this.options = [];
      this.output.replaceChildren();
      this.output.hidden = true;
      this.output.removeAttribute('aria-busy');
      this.initial.hidden = false;
      this.status.textContent = '';
      this.input.setAttribute('aria-expanded', 'false');
    }

    async search(query) {
      this.controller?.abort();
      const requestId = ++this.requestId;
      this.setActive(-1);
      this.options = [];
      this.initial.hidden = true;
      this.output.hidden = false;
      this.output.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.loading;
      if (!this.output.children.length) {
        const loading = document.createElement('p');
        loading.className = 'nh-search-empty';
        loading.textContent = this.dataset.loading;
        this.output.append(loading);
      }
      try {
        let markup = this.cache.get(query);
        if (!markup) {
          this.controller = new AbortController();
          const url = new URL(this.dataset.endpoint, window.location.origin);
          url.searchParams.set('q', query);
          url.searchParams.set('section_id', 'nh-predictive-search');
          url.searchParams.set('resources[type]', 'product,collection,query');
          url.searchParams.set('resources[limit]', '8');
          const response = await fetch(url, { signal: this.controller.signal });
          if (!response.ok) throw new Error('Search unavailable');
          markup = await response.text();
        }
        if (requestId !== this.requestId || query !== this.input.value.trim()) return;
        const fragment = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-nh-search-response]');
        if (!fragment) throw new Error('Search unavailable');
        if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(query, markup);
        this.output.replaceChildren(fragment);
        this.options = Array.from(this.output.querySelectorAll('[data-nh-search-option]'));
        this.input.setAttribute('aria-expanded', String(this.options.length > 0));
        this.status.textContent = this.dataset.resultStatus.replace('[count]', fragment.dataset.resultCount || '0');
      } catch (error) {
        if (error.name === 'AbortError' || requestId !== this.requestId || query !== this.input.value.trim()) return;
        const message = document.createElement('p');
        message.className = 'nh-search-empty';
        message.textContent = this.dataset.error;
        const link = document.createElement('a');
        link.className = 'nh-search-view-all';
        const url = new URL(this.dataset.searchUrl, window.location.origin);
        url.searchParams.set('q', query);
        link.href = url.href;
        link.textContent = this.dataset.viewAll;
        this.output.replaceChildren(message, link);
        this.input.setAttribute('aria-expanded', 'false');
        this.status.textContent = this.dataset.error;
      } finally {
        if (requestId === this.requestId) this.output.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('nh-search-form', NhSearchForm);
}
